import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixLoyaltyV1Coupon from './loyalty-v1-coupon-coupons.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * A loyalty coupon is created when a customer redeems their loyalty points for a reward. Creating a loyalty coupon
 * also creates a corresponding "reference" coupon with the Coupons API.
 */
export interface LoyaltyCoupon {
  /**
   * Loyalty coupon ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Loyalty [account ID](https://dev.wix.com/docs/rest/crm/loyalty-program/accounts/account-object) of the customer that redeemed points for a coupon.
   * @format GUID
   * @readonly
   */
  accountId?: string;
  /**
   * Member ID of the customer that redeemed points for a coupon. See the Members API for more information.
   * @format GUID
   * @readonly
   * @deprecated Member ID of the customer that redeemed points for a coupon. See the Members API for more information.
   * @replacedBy member_id
   * @targetRemovalDate 2024-06-01
   */
  memberIdDeprecated?: string;
  /**
   * Member ID of the customer that redeemed points for a coupon. See the Members API for more information.
   * @format GUID
   * @readonly
   */
  memberId?: string | null;
  /**
   * [Transaction ID](https://dev.wix.com/docs/rest/crm/loyalty-program/transactions/loyalty-transaction-object) for the transaction that created a coupon.
   * @format GUID
   * @readonly
   */
  transactionId?: string | null;
  /**
   * Reference coupon information for the corresponding [coupon](https://dev.wix.com/docs/rest/business-management/marketing/coupons/coupons/coupon-object)
   * that is created along with the loyalty coupon.
   * @readonly
   */
  couponReference?: CouponReference;
  /**
   * Loyalty coupon status.
   *
   * This status relates to the corresponding coupon that is created
   * at the same time as the loyalty coupon and is included in `couponReference`.
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Name of reward that was redeemed to create this coupon.
   * @minLength 1
   * @maxLength 50
   * @readonly
   */
  rewardName?: string;
  /**
   * Revision number, which increments by 1 each time the loyalty coupon is updated.
   *
   * To prevent conflicting changes, the current `revision` must be passed when updating the loyalty coupon.
   * @immutable
   */
  revision?: string | null;
  /**
   * Date and time the loyalty coupon was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the loyalty coupon was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Whether the loyalty coupon was created in a "refundable workflow" i.e. checkout-exchange, and therefore can be deleted in the background. Refundable coupons should not be visible to customers in the UI.
   * @readonly
   */
  refundable?: boolean | null;
}

export interface CouponReference {
  /**
   * Coupon ID.
   * @format GUID
   * @readonly
   */
  couponId?: string;
  /**
   * Coupon code.
   *
   * Unique code entered by a customer to apply the coupon.
   * @maxLength 20
   * @readonly
   */
  code?: string;
  /**
   * Name of coupon.
   * @maxLength 80
   * @readonly
   */
  name?: string | null;
  /**
   * The information to use when creating the coupon.
   * @readonly
   */
  specification?: Specification;
  /**
   * Whether the referenced coupon was deleted.
   * @readonly
   */
  deleted?: boolean | null;
}

export interface Specification
  extends SpecificationTypeDetailsOneOf,
    SpecificationScopeOrMinSubtotalOneOf {
  /**
   * Fixed price discount.
   * @min 0.01
   */
  moneyOffAmount?: number;
  /** Discount as a percentage. */
  percentOffRate?: number;
  /** Free shipping. If true, the coupon applies to all items in all `namespaces` on a site. */
  freeShipping?: boolean;
  /** Specific sale price. Currently only supported for coupons with a `stores` `namespace`. */
  fixedPriceAmount?: number;
  /**
   * Free products when making a purchase. `buyXGetY` is an object that specifies `x` and `y` in the
   * following scenario: if a visitor purchases x number of products, they receive y number of products for free. C
   * urrently only supported for coupons with a `stores` `namespace`.
   */
  buyXGetY?: BuyXGetY;
  /**
   * Scope of the coupon. When no scope is defined, the coupon applies to all
   * items in all `namespaces` in the site.
   */
  scope?: Scope;
  /** The coupon is only applicable when the order subtotal is over this amount. */
  minimumSubtotal?: number | null;
  /** Name of coupon. */
  name?: string | null;
  type?: TypeWithLiterals;
  /**
   * Whether the coupon is limited to 1 discount per order. If true and a customer pays for multiple items
   * that the coupon applies to, only the lowest priced item is discounted.
   * Coupons with a `bookings` `namespace` are always limited to 1 item.
   */
  limitedToOneItem?: boolean | null;
  /** Whether the coupon also applies to subscriptions. */
  appliesToSubscriptions?: boolean | null;
  /**
   * Specifies the amount of cycles to apply the discount to for a subscription item.
   *
   * Can only be set when `appliesToSubscriptions` is `TRUE` and `specification.scope.namespace` is `pricingPlans`.
   * If `discountedCycleCount` is empty, the coupon applies to all available cycles.
   *
   * Min: `1`
   *
   * Max: `999`
   */
  discountedCycleCount?: number | null;
}

/** @oneof */
export interface SpecificationTypeDetailsOneOf {
  /**
   * Fixed price discount.
   * @min 0.01
   */
  moneyOffAmount?: number;
  /** Discount as a percentage. */
  percentOffRate?: number;
  /** Free shipping. If true, the coupon applies to all items in all `namespaces` on a site. */
  freeShipping?: boolean;
  /** Specific sale price. Currently only supported for coupons with a `stores` `namespace`. */
  fixedPriceAmount?: number;
  /**
   * Free products when making a purchase. `buyXGetY` is an object that specifies `x` and `y` in the
   * following scenario: if a visitor purchases x number of products, they receive y number of products for free. C
   * urrently only supported for coupons with a `stores` `namespace`.
   */
  buyXGetY?: BuyXGetY;
}

/** @oneof */
export interface SpecificationScopeOrMinSubtotalOneOf {
  /**
   * Scope of the coupon. When no scope is defined, the coupon applies to all
   * items in all `namespaces` in the site.
   */
  scope?: Scope;
  /** The coupon is only applicable when the order subtotal is over this amount. */
  minimumSubtotal?: number | null;
}

export enum Type {
  UNKNOWN = 'UNKNOWN',
  MONEY_OFF_AMOUNT = 'MONEY_OFF_AMOUNT',
  PERCENT_OFF_RATE = 'PERCENT_OFF_RATE',
  FREE_SHIPPING = 'FREE_SHIPPING',
  FIXED_PRICE_AMOUNT = 'FIXED_PRICE_AMOUNT',
  BUY_X_GET_Y = 'BUY_X_GET_Y',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNKNOWN'
  | 'MONEY_OFF_AMOUNT'
  | 'PERCENT_OFF_RATE'
  | 'FREE_SHIPPING'
  | 'FIXED_PRICE_AMOUNT'
  | 'BUY_X_GET_Y';

export interface BuyXGetY {
  /** Number of purchased items required to receive free items. */
  x?: number;
  /** Number of items received for free if required number of items were purchased. */
  y?: number;
}

export interface Scope {
  /**
   * Group within a `namespace` for which the coupon is applicable.
   *
   * If no group is specified, the coupon applies to all items in the namespace.
   * `group` is required in some namespaces. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)
   * for a list of currently supported groups for each namespace.
   */
  name?: string | null;
  /**
   * ID of the specific entity in the group for which the coupon is applicable.
   *
   * If no `entityId` is specified, the coupon applies to all entities in the group. In some cases when a group is specified,
   * an `entityId` is required. See [Scope Values](https://dev.wix.com/api/rest/coupons/coupons/valid-scope-values)
   * for a list of currently supported entities for each namespace and group.
   */
  entityId?: string | null;
  /**
   * Wix application for which the coupon is applicable.
   *
   * One of the following:
   * + `"stores"`
   * + `"bookings"`
   * + `"events"`
   * + `"pricingPlans"`
   */
  namespace?: string;
}

export enum Status {
  /** Unknown status. */
  UNKNOWN = 'UNKNOWN',
  /** The reference coupon was created but the loyalty points have not been redeemed yet. */
  PENDING = 'PENDING',
  /** The reference coupon is active and available to the customer. */
  ACTIVE = 'ACTIVE',
  /** The reference coupon was applied and is no longer available for use. */
  APPLIED = 'APPLIED',
  /** The reference coupon was created but something went wrong when redeeming points from the loyalty account. */
  FAILED = 'FAILED',
  /** The reference coupon was deleted. */
  ARCHIVED = 'ARCHIVED',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'UNKNOWN'
  | 'PENDING'
  | 'ACTIVE'
  | 'APPLIED'
  | 'FAILED'
  | 'ARCHIVED';

export interface RedeemPointsForCouponRequest {
  /**
   * ID of the loyalty reward to redeem. See the Loyalty Rewards API for more information.
   * @format GUID
   */
  rewardId: string;
  /**
   * ID of the loyalty account of the customer redeeming points. See the Loyalty Accounts API for more information.
   * @format GUID
   */
  loyaltyAccountId: string;
}

export interface RedeemPointsForCouponResponse {
  /** Created loyalty coupon. */
  coupon?: LoyaltyCoupon;
}

export interface RedeemCurrentMemberPointsForCouponRequest {
  /**
   * ID of the loyalty reward to redeem. See the Loyalty Rewards API for more information.
   * @format GUID
   */
  rewardId: string;
}

export interface RedeemCurrentMemberPointsForCouponResponse {
  /** Created loyalty coupon. */
  coupon?: LoyaltyCoupon;
}

export interface RedeemMemberPointsForDiscountAmountCouponRequest {
  /**
   * ID of the loyalty reward to redeem. See the Loyalty Rewards API for more information.
   * @format GUID
   */
  rewardId?: string;
  /**
   * Loyalty account ID. See the Loyalty Accounts API for more information.
   * @format GUID
   */
  loyaltyAccountId?: string;
  /** Number of points to redeem. */
  pointsToRedeem?: number;
  /** Coupon specification. */
  specification?: Specification;
}

export interface RedeemMemberPointsForDiscountAmountCouponResponse {
  /** Created loyalty coupon. */
  coupon?: LoyaltyCoupon;
  /** Transaction ID of the coupon redemption. */
  transactionId?: string;
}

export interface GetLoyaltyCouponRequest {
  /**
   * ID of the loyalty coupon to retrieve.
   * @format GUID
   */
  loyaltyCouponId: string;
}

export interface GetLoyaltyCouponResponse {
  /** Retrieved loyalty coupon. */
  loyaltyCoupon?: LoyaltyCoupon;
}

export interface BulkGetLoyaltyCouponRequest {
  /** Query to filter loyalty coupons. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface BulkGetLoyaltyCouponResponse {
  /**
   * Retrieved loyalty coupons.
   * @maxSize 100
   */
  couponsInSite?: CouponsInSite[];
}

export interface CouponsInSite {
  /**
   * Metasite ID.
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Retrieved loyalty coupons.
   * @maxSize 100
   */
  loyaltyCoupons?: LoyaltyCoupon[];
}

export interface GetCurrentMemberCouponsRequest {}

export interface GetCurrentMemberCouponsResponse {
  /** Retrieved loyalty coupons. */
  loyaltyCoupons?: LoyaltyCoupon[];
}

export interface QueryLoyaltyCouponRequest {
  /** Query options. */
  query: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   */
  sort?: Sorting[];
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface QueryLoyaltyCouponResponse {
  /** Retrieved loyalty coupons. */
  loyaltyCoupons?: LoyaltyCoupon[];
  /** Metadata. */
  metadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface DeleteLoyaltyCouponRequest {
  /**
   * ID of the loyalty coupon to delete.
   * @format GUID
   */
  _id: string;
  /**
   * Revision number, which increments by 1 each time the loyalty coupon is updated.
   *
   * To prevent conflicting changes, the current `revision` must be passed when updating the loyalty coupon.
   */
  revision?: string;
}

export interface DeleteLoyaltyCouponResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface CouponCreatedEnvelope {
  entity: LoyaltyCoupon;
  metadata: EventMetadata;
}

/**
 * Triggered when a loyalty coupon is created.
 * @permissionScope Read Loyalty
 * @permissionScopeId SCOPE.DC-LOYALTY.READ-LOYALTY
 * @permissionScope Manage Loyalty
 * @permissionScopeId SCOPE.DC-LOYALTY.MANAGE-LOYALTY
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId LOYALTY.READ_COUPONS
 * @webhook
 * @eventType wix.loyalty.v1.coupon_created
 * @serviceIdentifier com.wixpress.loyalty.coupon.LoyaltyCoupons
 * @slug created
 */
export declare function onCouponCreated(
  handler: (event: CouponCreatedEnvelope) => void | Promise<void>
): void;

export interface CouponDeletedEnvelope {
  metadata: EventMetadata;
}

/**
 * Triggered when a loyalty coupon is deleted.
 * @permissionScope Read Loyalty
 * @permissionScopeId SCOPE.DC-LOYALTY.READ-LOYALTY
 * @permissionScope Manage Loyalty
 * @permissionScopeId SCOPE.DC-LOYALTY.MANAGE-LOYALTY
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionId LOYALTY.READ_COUPONS
 * @webhook
 * @eventType wix.loyalty.v1.coupon_deleted
 * @serviceIdentifier com.wixpress.loyalty.coupon.LoyaltyCoupons
 * @slug deleted
 */
export declare function onCouponDeleted(
  handler: (event: CouponDeletedEnvelope) => void | Promise<void>
): void;

/**
 * Redeems a customer's loyalty points for a loyalty reward and creates a loyalty coupon.
 *
 * Creating a loyalty coupon also creates a corresponding "reference" coupon with the [Coupons API](https://dev.wix.com/docs/rest/business-management/marketing/coupons/coupons/coupon-object).
 * The customer receives the reference coupon, which they can apply to their order. The loyalty coupon and its corresponding reference coupon
 * are linked and the loyalty coupon's `status` reflects the current state of the reference coupon.
 *
 * Check which loyalty rewards a site has available with List Rewards in the Loyalty Rewards API.
 * @param rewardId - ID of the loyalty reward to redeem. See the Loyalty Rewards API for more information.
 * @public
 * @requiredField options
 * @requiredField options.loyaltyAccountId
 * @requiredField rewardId
 * @permissionId LOYALTY.REDEEM_COUPON
 * @applicableIdentity APP
 * @fqn com.wixpress.loyalty.coupon.LoyaltyCoupons.RedeemPointsForCoupon
 */
export async function redeemPointsForCoupon(
  rewardId: string,
  options: NonNullablePaths<RedeemPointsForCouponOptions, `loyaltyAccountId`, 2>
): Promise<
  NonNullablePaths<
    RedeemPointsForCouponResponse,
    | `coupon._id`
    | `coupon.accountId`
    | `coupon.memberIdDeprecated`
    | `coupon.couponReference.couponId`
    | `coupon.couponReference.code`
    | `coupon.couponReference.specification.moneyOffAmount`
    | `coupon.couponReference.specification.percentOffRate`
    | `coupon.couponReference.specification.freeShipping`
    | `coupon.couponReference.specification.fixedPriceAmount`
    | `coupon.couponReference.specification.buyXGetY.x`
    | `coupon.couponReference.specification.buyXGetY.y`
    | `coupon.couponReference.specification.scope.namespace`
    | `coupon.couponReference.specification.type`
    | `coupon.status`
    | `coupon.rewardName`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    rewardId: rewardId,
    loyaltyAccountId: options?.loyaltyAccountId,
  });

  const reqOpts = ambassadorWixLoyaltyV1Coupon.redeemPointsForCoupon(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          rewardId: '$[0]',
          loyaltyAccountId: '$[1].loyaltyAccountId',
        },
        singleArgumentUnchanged: false,
      },
      ['rewardId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RedeemPointsForCouponOptions {
  /**
   * ID of the loyalty account of the customer redeeming points. See the Loyalty Accounts API for more information.
   * @format GUID
   */
  loyaltyAccountId: string;
}

/**
 * Redeems a current customer's loyalty points for a loyalty reward and creates a loyalty coupon.
 *
 * Creating a loyalty coupon also creates a corresponding "reference" coupon with the [Coupons API](https://dev.wix.com/docs/rest/business-management/marketing/coupons/coupons/coupon-object).
 * The customer receives the reference coupon, which they can apply to their order. The loyalty coupon and its corresponding reference coupon
 * are linked and the loyalty coupon's `status` reflects the current state of the reference coupon.
 *
 * Check which loyalty rewards a site has available with List Rewards in the Loyalty Rewards API.
 *
 * >**Note:**
 * >This endpoint requires [visitor or member authentication](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities).
 * @param rewardId - ID of the loyalty reward to redeem. See the Loyalty Rewards API for more information.
 * @public
 * @requiredField rewardId
 * @permissionId LOYALTY.REDEEM_OWN_COUPON
 * @applicableIdentity APP
 * @applicableIdentity MEMBER
 * @fqn com.wixpress.loyalty.coupon.LoyaltyCoupons.RedeemCurrentMemberPointsForCoupon
 */
export async function redeemCurrentMemberPointsForCoupon(
  rewardId: string
): Promise<
  NonNullablePaths<
    RedeemCurrentMemberPointsForCouponResponse,
    | `coupon._id`
    | `coupon.accountId`
    | `coupon.memberIdDeprecated`
    | `coupon.couponReference.couponId`
    | `coupon.couponReference.code`
    | `coupon.couponReference.specification.moneyOffAmount`
    | `coupon.couponReference.specification.percentOffRate`
    | `coupon.couponReference.specification.freeShipping`
    | `coupon.couponReference.specification.fixedPriceAmount`
    | `coupon.couponReference.specification.buyXGetY.x`
    | `coupon.couponReference.specification.buyXGetY.y`
    | `coupon.couponReference.specification.scope.namespace`
    | `coupon.couponReference.specification.type`
    | `coupon.status`
    | `coupon.rewardName`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ rewardId: rewardId });

  const reqOpts =
    ambassadorWixLoyaltyV1Coupon.redeemCurrentMemberPointsForCoupon(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { rewardId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['rewardId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a loyalty coupon.
 * @param loyaltyCouponId - ID of the loyalty coupon to retrieve.
 * @public
 * @requiredField loyaltyCouponId
 * @permissionId LOYALTY.READ_COUPONS
 * @applicableIdentity APP
 * @returns Retrieved loyalty coupon.
 * @fqn com.wixpress.loyalty.coupon.LoyaltyCoupons.GetLoyaltyCoupon
 */
export async function getLoyaltyCoupon(
  loyaltyCouponId: string
): Promise<
  NonNullablePaths<
    LoyaltyCoupon,
    | `_id`
    | `accountId`
    | `memberIdDeprecated`
    | `couponReference.couponId`
    | `couponReference.code`
    | `couponReference.specification.moneyOffAmount`
    | `couponReference.specification.percentOffRate`
    | `couponReference.specification.freeShipping`
    | `couponReference.specification.fixedPriceAmount`
    | `couponReference.specification.buyXGetY.x`
    | `couponReference.specification.buyXGetY.y`
    | `couponReference.specification.scope.namespace`
    | `couponReference.specification.type`
    | `status`
    | `rewardName`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    loyaltyCouponId: loyaltyCouponId,
  });

  const reqOpts = ambassadorWixLoyaltyV1Coupon.getLoyaltyCoupon(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.loyaltyCoupon!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { loyaltyCouponId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['loyaltyCouponId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a sequence of loyalty coupons.
 * @public
 * @permissionId LOYALTY.REWARD_BULK_READ
 * @fqn com.wixpress.loyalty.coupon.LoyaltyCoupons.BulkGetLoyaltyCoupon
 */
export async function bulkGetLoyaltyCoupon(
  options?: BulkGetLoyaltyCouponOptions
): Promise<
  NonNullablePaths<
    BulkGetLoyaltyCouponResponse,
    `couponsInSite` | `couponsInSite.${number}.metaSiteId`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: options?.query,
  });

  const reqOpts = ambassadorWixLoyaltyV1Coupon.bulkGetLoyaltyCoupon(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0].query' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkGetLoyaltyCouponOptions {
  /** Query to filter loyalty coupons. */
  query?: CursorQuery;
}

/**
 * Retrieves the loyalty coupons for the currently logged-in member.
 *
 * >**Note:**
 * >This method requires [visitor or member authentication](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).
 * @public
 * @permissionId LOYALTY.READ_OWN_COUPONS
 * @applicableIdentity APP
 * @applicableIdentity MEMBER
 * @fqn com.wixpress.loyalty.coupon.LoyaltyCoupons.GetCurrentMemberCoupons
 */
export async function getCurrentMemberCoupons(): Promise<
  NonNullablePaths<
    GetCurrentMemberCouponsResponse,
    | `loyaltyCoupons`
    | `loyaltyCoupons.${number}._id`
    | `loyaltyCoupons.${number}.accountId`
    | `loyaltyCoupons.${number}.memberIdDeprecated`
    | `loyaltyCoupons.${number}.couponReference.couponId`
    | `loyaltyCoupons.${number}.couponReference.code`
    | `loyaltyCoupons.${number}.couponReference.specification.moneyOffAmount`
    | `loyaltyCoupons.${number}.couponReference.specification.percentOffRate`
    | `loyaltyCoupons.${number}.couponReference.specification.freeShipping`
    | `loyaltyCoupons.${number}.couponReference.specification.fixedPriceAmount`
    | `loyaltyCoupons.${number}.couponReference.specification.type`
    | `loyaltyCoupons.${number}.status`
    | `loyaltyCoupons.${number}.rewardName`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts = ambassadorWixLoyaltyV1Coupon.getCurrentMemberCoupons(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of loyalty coupons, with the specified paging, filtering, and sorting.
 * @public
 * @permissionId LOYALTY.READ_COUPONS
 * @applicableIdentity APP
 * @fqn com.wixpress.loyalty.coupon.LoyaltyCoupons.QueryLoyaltyCoupon
 */
export function queryLoyaltyCoupons(): LoyaltyCouponsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    LoyaltyCoupon,
    'CURSOR',
    QueryLoyaltyCouponRequest,
    QueryLoyaltyCouponResponse
  >({
    func: async (payload: QueryLoyaltyCouponRequest) => {
      const reqOpts = ambassadorWixLoyaltyV1Coupon.queryLoyaltyCoupon(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryLoyaltyCouponRequest['query']) => {
      const args = [query, {}] as [QueryLoyaltyCouponRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryLoyaltyCouponResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.loyaltyCoupons,
        pagingMetadata: transformedData?.metadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface LoyaltyCouponsQueryResult extends QueryCursorResult {
  items: LoyaltyCoupon[];
  query: LoyaltyCouponsQueryBuilder;
  next: () => Promise<LoyaltyCouponsQueryResult>;
  prev: () => Promise<LoyaltyCouponsQueryResult>;
}

export interface LoyaltyCouponsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | 'accountId'
      | 'memberId'
      | 'transactionId'
      | 'couponReference'
      | 'status'
      | 'rewardName'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => LoyaltyCouponsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | 'accountId'
      | 'memberId'
      | 'transactionId'
      | 'couponReference'
      | 'status'
      | 'rewardName'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => LoyaltyCouponsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | 'accountId'
      | 'memberId'
      | 'transactionId'
      | 'rewardName'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => LoyaltyCouponsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | 'accountId'
      | 'memberId'
      | 'transactionId'
      | 'rewardName'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => LoyaltyCouponsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | 'accountId'
      | 'memberId'
      | 'transactionId'
      | 'rewardName'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => LoyaltyCouponsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | 'accountId'
      | 'memberId'
      | 'transactionId'
      | 'rewardName'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => LoyaltyCouponsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: 'accountId' | 'memberId' | 'transactionId' | 'rewardName',
    value: string
  ) => LoyaltyCouponsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | 'accountId'
      | 'memberId'
      | 'transactionId'
      | 'couponReference'
      | 'status'
      | 'rewardName'
      | '_createdDate'
      | '_updatedDate',
    value: any[]
  ) => LoyaltyCouponsQueryBuilder;
  in: (
    propertyName:
      | 'accountId'
      | 'memberId'
      | 'transactionId'
      | 'couponReference'
      | 'status'
      | 'rewardName'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => LoyaltyCouponsQueryBuilder;
  exists: (
    propertyName:
      | 'accountId'
      | 'memberId'
      | 'transactionId'
      | 'couponReference'
      | 'status'
      | 'rewardName'
      | '_createdDate'
      | '_updatedDate',
    value: boolean
  ) => LoyaltyCouponsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | 'accountId'
      | 'memberId'
      | 'transactionId'
      | 'couponReference'
      | 'status'
      | 'rewardName'
      | '_createdDate'
      | '_updatedDate'
    >
  ) => LoyaltyCouponsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | 'accountId'
      | 'memberId'
      | 'transactionId'
      | 'couponReference'
      | 'status'
      | 'rewardName'
      | '_createdDate'
      | '_updatedDate'
    >
  ) => LoyaltyCouponsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => LoyaltyCouponsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => LoyaltyCouponsQueryBuilder;
  find: () => Promise<LoyaltyCouponsQueryResult>;
}

/**
 * @hidden
 * @fqn com.wixpress.loyalty.coupon.LoyaltyCoupons.QueryLoyaltyCoupon
 * @requiredField query
 */
export async function typedQueryLoyaltyCoupons(
  query: LoyaltyCouponQuery
): Promise<
  NonNullablePaths<
    QueryLoyaltyCouponResponse,
    | `loyaltyCoupons`
    | `loyaltyCoupons.${number}._id`
    | `loyaltyCoupons.${number}.accountId`
    | `loyaltyCoupons.${number}.memberIdDeprecated`
    | `loyaltyCoupons.${number}.couponReference.couponId`
    | `loyaltyCoupons.${number}.couponReference.code`
    | `loyaltyCoupons.${number}.couponReference.specification.moneyOffAmount`
    | `loyaltyCoupons.${number}.couponReference.specification.percentOffRate`
    | `loyaltyCoupons.${number}.couponReference.specification.freeShipping`
    | `loyaltyCoupons.${number}.couponReference.specification.fixedPriceAmount`
    | `loyaltyCoupons.${number}.couponReference.specification.type`
    | `loyaltyCoupons.${number}.status`
    | `loyaltyCoupons.${number}.rewardName`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixLoyaltyV1Coupon.queryLoyaltyCoupon(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface LoyaltyCouponQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_createdDate',
        '_updatedDate',
        'accountId',
        'couponReference',
        'memberId',
        'rewardName',
        'status',
        'transactionId'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  LoyaltyCoupon,
  LoyaltyCouponQuerySpec
>;
export type LoyaltyCouponQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      LoyaltyCoupon,
      LoyaltyCouponQuerySpec,
      LoyaltyCouponQuery
    >(),
  },
};

/**
 * Deletes a loyalty coupon.
 *
 * The deletion of a loyalty coupon does not impact the functionality of the corresponding coupon itself.
 * @param _id - ID of the loyalty coupon to delete.
 * @param revision - Revision number, which increments by 1 each time the loyalty coupon is updated.
 *
 * To prevent conflicting changes, the current `revision` must be passed when updating the loyalty coupon.
 * @public
 * @requiredField _id
 * @requiredField revision
 * @permissionId LOYALTY.MANAGE_COUPONS
 * @applicableIdentity APP
 * @fqn com.wixpress.loyalty.coupon.LoyaltyCoupons.DeleteLoyaltyCoupon
 */
export async function deleteLoyaltyCoupon(
  _id: string,
  revision: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    revision: revision,
  });

  const reqOpts = ambassadorWixLoyaltyV1Coupon.deleteLoyaltyCoupon(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]', revision: '$[1]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'revision']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
